import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from 'Context/CartContext';
import { useCountry } from 'Context/CountryContext';
import { countryName, NON_EU_CODE } from 'Utils/euCountries';
import { apiClient, assetUrl } from 'Utils/api';
import Header from 'View/Component/Header/Header';
import Footer from 'View/Component/Footer/Footer';
import * as S from './CheckoutReviewPage.style';

interface ShippingZone {
  id: string;
  name: string;
  countryCodes: string | string[];
  baseRateCents: number;
  freeShippingThresholdCents: number | null;
  isActive: boolean;
}

interface PickupLocation {
  enabled: boolean;
  companyName: string;
  street: string;
  postalCode: string;
  city: string;
  countryCode: string;
  contactHint: { de: string; en: string };
}

const generateUuid = (): string => {
  if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
    return (crypto as any).randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const CheckoutReviewPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { items, total, clearCart } = useCart();
  const { setCountry } = useCountry();
  const [formData, setFormData] = useState<any>(null);
  const [zones, setZones] = useState<ShippingZone[]>([]);
  const [shippingZone, setShippingZone] = useState<ShippingZone | null>(null);
  const [pickup, setPickup] = useState<PickupLocation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [discountInput, setDiscountInput] = useState('');
  const [discountStatus, setDiscountStatus] = useState<'idle' | 'loading' | 'valid' | 'invalid'>('idle');
  const [discountError, setDiscountError] = useState('');
  const [applied, setApplied] = useState<{ code: string; discountCents: number; valueType: string; value: number } | null>(null);

  const isPickup = formData?.deliveryMethod === 'PICKUP';
  const pickupPayment: 'SEPA' | 'ONSITE' = formData?.pickupPayment === 'ONSITE' ? 'ONSITE' : 'SEPA';
  const effectivePaymentMethod = isPickup
    ? (pickupPayment === 'ONSITE' ? 'PICKUP_ONSITE' : 'SEPA')
    : 'SEPA';

  // The country that drives VAT/export pricing: shipping destination for a
  // separate shipping address, otherwise the billing country.
  const deliveryCountryCode =
    !isPickup && formData?.useDifferentShipping ? formData?.shippingCountry : formData?.country;
  const billingCountryDisplay =
    formData?.country === NON_EU_CODE
      ? (formData?.countryManual || countryName(NON_EU_CODE, i18n.language))
      : countryName(formData?.country || 'DE', i18n.language);
  const shippingCountryDisplay =
    formData?.shippingCountry === NON_EU_CODE
      ? (formData?.shippingCountryManual || countryName(NON_EU_CODE, i18n.language))
      : countryName(formData?.shippingCountry || 'DE', i18n.language);

  // Keep the global delivery country (drives the cart total shown here) aligned
  // with the country chosen on the address step so display == charged amount.
  useEffect(() => {
    if (deliveryCountryCode) setCountry(deliveryCountryCode);
  }, [deliveryCountryCode, setCountry]);

  useEffect(() => {
    const savedData = sessionStorage.getItem('checkoutFormData');
    if (!savedData) {
      navigate('/checkout/adresse');
      return;
    }
    setFormData(JSON.parse(savedData));
  }, [navigate]);

  useEffect(() => {
    if (!submitted && items.length === 0) {
      navigate('/warenkorb');
    }
  }, [items, navigate, submitted]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get(`/api/shipping-zones`);
        if (res.data?.shippingZones) {
          setZones(res.data.shippingZones);
        }
      } catch (e) {
        console.error('Failed to load shipping zones', e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get(`/api/pickup-location`);
        if (res.data?.pickup) setPickup(res.data.pickup);
      } catch (e) {
        /* pickup optional */
      }
    })();
  }, []);

  // Resolve shipping cost via tier-aware backend endpoint (uses cart subtotal)
  const [resolvedShippingCents, setResolvedShippingCents] = useState<number | null>(null);
  useEffect(() => {
    if (!formData || isPickup || !shippingZone) return;
    const country = formData.useDifferentShipping ? formData.shippingCountry : formData.country;
    const subtotalCents = Math.round(total * 100);
    (async () => {
      try {
        const res = await apiClient.get(`/api/shipping-zones-with-tiers`, {
          params: { countryCode: country, subtotalCents },
        });
        const match = (res.data?.shippingZones || []).find((z: any) => z.id === shippingZone.id)
          || res.data?.shippingZones?.[0];
        if (match && typeof match.appliedRateCents === 'number') {
          setResolvedShippingCents(match.appliedRateCents);
        }
      } catch (e) {
        setResolvedShippingCents(null);
      }
    })();
  }, [formData, shippingZone, total, isPickup]);

  useEffect(() => {
    if (!formData || isPickup || zones.length === 0) return;
    const country = formData.useDifferentShipping ? formData.shippingCountry : formData.country;
    const parseCodes = (z: ShippingZone): string[] => {
      if (Array.isArray(z.countryCodes)) return z.countryCodes;
      try { return JSON.parse(z.countryCodes as string); } catch { return []; }
    };
    let match = zones.find((z) => parseCodes(z).includes(country));
    if (!match) match = zones.find((z) => parseCodes(z).includes('*'));
    setShippingZone(match || null);
  }, [formData, zones, isPickup]);

  const subtotal = total;
  const hasExport = items.some((it: any) => it.isExport);
  const taxSchemes = Array.from(new Set(items.map((it: any) => it.taxScheme || 'MARGIN_25A_USTG')));
  const taxNoticeKey = hasExport
    ? 'watch_detail.tax_export'
    : taxSchemes.length === 0
      ? null
      : taxSchemes.length > 1
        ? 'checkout.tax_scheme_mixed'
        : taxSchemes[0] === 'STANDARD_VAT_19'
          ? 'checkout.tax_scheme_standard'
          : 'checkout.tax_scheme_margin';
  const shippingCost = isPickup
    ? 0
    : resolvedShippingCents !== null
      ? resolvedShippingCents / 100
      : shippingZone
        ? (shippingZone.freeShippingThresholdCents && subtotal * 100 >= shippingZone.freeShippingThresholdCents
            ? 0
            : shippingZone.baseRateCents / 100)
        : 0;
  const discount = applied ? applied.discountCents / 100 : 0;
  const grandTotal = Math.max(0, subtotal + shippingCost - discount);

  const applyDiscount = async () => {
    const code = discountInput.trim();
    if (!code) return;
    setDiscountStatus('loading');
    setDiscountError('');
    try {
      const subtotalCents = Math.round(subtotal * 100);
      const res = await apiClient.post(`/api/checkout/validate-code`, { code, subtotalCents });
      const r = res.data;
      if (r?.valid) {
        setApplied({ code: r.code, discountCents: Number(r.discountCents) || 0, valueType: r.valueType, value: Number(r.value) || 0 });
        setDiscountStatus('valid');
      } else {
        setApplied(null);
        setDiscountStatus('invalid');
        setDiscountError(t(`checkout.discount.reason_${r?.reason || 'NOT_FOUND'}`, { defaultValue: t('checkout.discount.invalid_generic') }));
      }
    } catch (e: any) {
      setApplied(null);
      setDiscountStatus('invalid');
      setDiscountError(t('checkout.discount.invalid_generic'));
    }
  };

  const removeDiscount = () => {
    setApplied(null);
    setDiscountInput('');
    setDiscountStatus('idle');
    setDiscountError('');
  };

  const handleConfirmOrder = async () => {
    if (!formData) return;
    if (!isPickup && !shippingZone) {
      setError(t('checkout.errors.shipping_zone_missing'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const watchIds = items.map((item) => item.id);
      const idempotencyKey = sessionStorage.getItem('checkoutIdempotencyKey') || generateUuid();
      sessionStorage.setItem('checkoutIdempotencyKey', idempotencyKey);

      const billingAddress = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        street: formData.street,
        city: formData.city,
        postalCode: formData.postalCode,
        countryCode: formData.country === NON_EU_CODE ? 'XX' : formData.country,
        countryName: formData.country === NON_EU_CODE ? (formData.countryManual || '') : undefined,
      };

      const shippingAddress = isPickup
        ? undefined
        : formData.useDifferentShipping
          ? {
              firstName: formData.shippingFirstName,
              lastName: formData.shippingLastName,
              street: formData.shippingStreet,
              city: formData.shippingCity,
              postalCode: formData.shippingPostalCode,
              countryCode: formData.shippingCountry === NON_EU_CODE ? 'XX' : formData.shippingCountry,
              countryName: formData.shippingCountry === NON_EU_CODE ? (formData.shippingCountryManual || '') : undefined,
            }
          : billingAddress;

      const orderData: any = {
        idempotencyKey,
        watchIds,
        customerEmail: formData.email,
        customerFirstName: formData.firstName,
        customerLastName: formData.lastName,
        customerPhone: formData.phone || undefined,
        deliveryMethod: isPickup ? 'PICKUP' : 'SHIPPING',
        customerNote: formData.customerNote || undefined,
        deliveryCountry: deliveryCountryCode,
        billingAddress,
        paymentMethod: effectivePaymentMethod,
      };
      if (!isPickup) {
        orderData.shippingZoneId = shippingZone!.id;
        orderData.shippingAddress = shippingAddress;
      }
      if (applied?.code) {
        orderData.discountCode = applied.code;
      }

      const response = await apiClient.post(`/api/checkout`, orderData);

      if (response.data?.order) {
        const order = response.data.order;
        sessionStorage.setItem('lastOrder', JSON.stringify({
          id: order.id,
          orderNumber: order.orderNumber,
          deliveryMethod: isPickup ? 'PICKUP' : 'SHIPPING',
          paymentMethod: effectivePaymentMethod,
        }));
        sessionStorage.removeItem('checkoutFormData');
        sessionStorage.removeItem('checkoutIdempotencyKey');

        setSubmitted(true);
        navigate('/checkout/bestaetigung', { replace: true });
        setTimeout(() => clearCart(), 100);
      } else {
        setError(response.data?.message || t('checkout.errors.order_failed'));
      }
    } catch (err: any) {
      console.error('Order error:', err);
      const msg = err.response?.data?.errors?.[0]?.errorMessage
        || err.response?.data?.message
        || err.message
        || t('checkout.errors.order_failed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!formData) {
    return null;
  }

  return (
    <>
      <Header />
      <S.PageContainer>
        <S.PageTitle>{t('checkout.review_title')}</S.PageTitle>

        <S.ContentGrid>
          <S.MainSection>
            <S.Section>
              <S.SectionTitle>{t('checkout.section_overview')}</S.SectionTitle>
              {items.map((item) => (
                <S.OrderItem key={item.id}>
                  <S.ItemImage>
                    {item.image ? (
                      <img src={assetUrl(item.image)} alt={`${item.brand} ${item.model}`} />
                    ) : (
                      <S.NoImage>{t('checkout.no_image')}</S.NoImage>
                    )}
                  </S.ItemImage>
                  <S.ItemDetails>
                    <S.ItemBrand>{item.brand}</S.ItemBrand>
                    <S.ItemModel>{item.model}</S.ItemModel>
                    {item.referenceNumber && <S.ItemRef>{t('checkout.ref_prefix')} {item.referenceNumber}</S.ItemRef>}
                  </S.ItemDetails>
                  <S.ItemPrice>{item.priceEur.toLocaleString('de-DE')} €</S.ItemPrice>
                </S.OrderItem>
              ))}
            </S.Section>

            <S.Section>
              <S.SectionTitle>{t('checkout.section_billing')}</S.SectionTitle>
              <S.AddressBox>
                <p>
                  {formData.firstName} {formData.lastName}
                </p>
                <p>{formData.street}</p>
                <p>
                  {formData.postalCode} {formData.city}
                </p>
                <p>{billingCountryDisplay}</p>
                <p>{formData.email}</p>
                {formData.phone && <p>{formData.phone}</p>}
              </S.AddressBox>
            </S.Section>

            <S.Section>
              <S.SectionTitle>{isPickup ? t('checkout.pickup_address_title') : t('checkout.section_shipping')}</S.SectionTitle>
              <S.AddressBox>
                {isPickup ? (
                  pickup ? (
                    <>
                      {pickup.companyName && <p>{pickup.companyName}</p>}
                      {pickup.street && <p>{pickup.street}</p>}
                      {(pickup.postalCode || pickup.city) && (
                        <p>{pickup.postalCode} {pickup.city}</p>
                      )}
                      {pickup.countryCode && (
                        <p>{t(`checkout.countries.${pickup.countryCode}`, { defaultValue: pickup.countryCode })}</p>
                      )}
                      <p style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>
                        {(pickup.contactHint?.[i18n.language as 'de' | 'en']) || pickup.contactHint?.de}
                      </p>
                    </>
                  ) : (
                    <p>{t('checkout.delivery_pickup')}</p>
                  )
                ) : formData.useDifferentShipping ? (
                  <>
                    <p>
                      {formData.shippingFirstName} {formData.shippingLastName}
                    </p>
                    <p>{formData.shippingStreet}</p>
                    <p>
                      {formData.shippingPostalCode} {formData.shippingCity}
                    </p>
                    <p>{shippingCountryDisplay}</p>
                  </>
                ) : (
                  <p>{t('checkout.shipping_same_as_billing')}</p>
                )}
              </S.AddressBox>
            </S.Section>

            {formData.customerNote && (
              <S.Section>
                <S.SectionTitle>{t('checkout.section_note')}</S.SectionTitle>
                <S.AddressBox data-testid="review-customer-note">
                  <p style={{ whiteSpace: 'pre-wrap' }}>{formData.customerNote}</p>
                </S.AddressBox>
              </S.Section>
            )}

            <S.Section>
              <S.SectionTitle>{t('checkout.section_payment')}</S.SectionTitle>
              <S.PaymentInfo>
                <span>
                  <img src="/assets/images/icons/sepa-bank.png" alt="SEPA" style={{ width: 30, height: 30, objectFit: 'contain', display: 'block' }} />
                </span>
                <div>
                  {isPickup ? (
                    pickupPayment === 'ONSITE' ? (
                      <>
                        <strong>{t('checkout.payment_method_onsite')}</strong>
                        <p>{t('checkout.payment_method_onsite_info')}</p>
                        <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
                          <li>{t('checkout.payment_method_onsite_option_realtime')}</li>
                          <li>{t('checkout.payment_method_onsite_option_cash')}</li>
                          <li>{t('checkout.payment_method_onsite_option_card')}</li>
                        </ul>
                      </>
                    ) : (
                      <>
                        <strong>{t('checkout.payment_method_sepa')}</strong>
                        <p>{t('checkout.payment_method_sepa_info')}</p>
                      </>
                    )
                  ) : (
                    <>
                      <strong>{t('checkout.payment_sepa')}</strong>
                      <p>{t('checkout.payment_sepa_info')}</p>
                    </>
                  )}
                </div>
              </S.PaymentInfo>
            </S.Section>
          </S.MainSection>

          <S.SummarySection>
            <S.SummaryTitle>{t('checkout.summary_title')}</S.SummaryTitle>
            <S.SummaryRow>
              <span>{t('checkout.subtotal')}</span>
              <span>{subtotal.toLocaleString('de-DE')} €</span>
            </S.SummaryRow>
            <S.SummaryRow>
              <span>{isPickup ? t('checkout.delivery_pickup') : `${t('checkout.shipping')}${shippingZone ? ` (${shippingZone.name})` : ''}`}</span>
              <span>
                {isPickup
                  ? t('checkout.shipping_free')
                  : shippingZone
                    ? (shippingCost === 0 ? t('checkout.shipping_free') : `${shippingCost.toLocaleString('de-DE')} €`)
                    : t('checkout.shipping_calculated')}
              </span>
            </S.SummaryRow>

            <S.DiscountBlock data-testid="discount-block">
              {!applied ? (
                <>
                  <S.DiscountInputRow>
                    <S.DiscountInput
                      placeholder={t('checkout.discount.placeholder')}
                      value={discountInput}
                      onChange={(e) => { setDiscountInput(e.target.value); setDiscountStatus('idle'); setDiscountError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyDiscount(); } }}
                      data-testid="discount-input"
                    />
                    <S.DiscountApplyButton
                      type="button"
                      onClick={applyDiscount}
                      disabled={discountStatus === 'loading' || !discountInput.trim()}
                      data-testid="discount-apply-btn"
                    >
                      {discountStatus === 'loading' ? '...' : t('checkout.discount.apply')}
                    </S.DiscountApplyButton>
                  </S.DiscountInputRow>
                  {discountError && (
                    <S.DiscountError data-testid="discount-error">{discountError}</S.DiscountError>
                  )}
                </>
              ) : (
                <S.DiscountAppliedRow data-testid="discount-applied">
                  <span>
                    {t('checkout.discount.applied_label', { defaultValue: 'Rabatt' })} ({applied.code})
                    {applied.valueType === 'PERCENT' ? ` · ${applied.value}%` : ''}
                  </span>
                  <S.DiscountAppliedRight>
                    <span>− {discount.toLocaleString('de-DE')} €</span>
                    <S.DiscountRemoveBtn type="button" onClick={removeDiscount} data-testid="discount-remove-btn">
                      ✕
                    </S.DiscountRemoveBtn>
                  </S.DiscountAppliedRight>
                </S.DiscountAppliedRow>
              )}
            </S.DiscountBlock>

            <S.Divider />
            <S.SummaryTotal>
              <span>{t('checkout.total')}</span>
              <span>{grandTotal.toLocaleString('de-DE')} €</span>
            </S.SummaryTotal>

            {taxNoticeKey && (
              <div
                data-testid="tax-notice"
                style={{
                  marginTop: '0.5rem',
                  fontSize: '0.85rem',
                  color: '#657455',
                  lineHeight: 1.4,
                }}
              >
                {t(taxNoticeKey)}
              </div>
            )}

            {error && <S.ErrorMessage>{error}</S.ErrorMessage>}

            <S.ConfirmButton onClick={handleConfirmOrder} disabled={loading || (!isPickup && !shippingZone)} data-testid="confirm-order-btn">
              {loading ? t('checkout.processing') : t('checkout.confirm_button')}
            </S.ConfirmButton>

            <S.BackButton onClick={() => navigate('/checkout/adresse')}>
              {t('checkout.back')}
            </S.BackButton>
          </S.SummarySection>
        </S.ContentGrid>
      </S.PageContainer>
      <Footer />
    </>
  );
};

export default CheckoutReviewPage;
